import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';
import {
  TPolicyListQueryOptions,
  TPolicyListResponse,
  TPolicyPeriodRequest,
  TTransferPricingPeriodSelection,
} from './types';

function appendListQuery(
  queryString: URLSearchParams,
  listQuery: TPolicyListQueryOptions | undefined
): void {
  if (!listQuery) return;

  const {
    pageSize,
    cursor,
    filters,
    includeSegmentCounts,
    includeFilterFacets,
    includeLineItems,
  } = listQuery;

  if (pageSize) queryString.set('paginationConfig[limit]', String(pageSize));
  if (cursor) queryString.set('paginationConfig[cursor]', cursor);

  if (includeSegmentCounts) queryString.set('includeSegmentCounts', 'true');

  if (includeFilterFacets) queryString.set('includeFilterFacets', 'true');

  if (includeLineItems) queryString.set('includeLineItems', 'true');

  if (!filters) return;

  if (filters.search) queryString.set('filterConfig[search]', filters.search);

  filters.transactionTypes?.forEach((transactionType) =>
    queryString.append('filterConfig[transactionTypes][]', transactionType)
  );
  filters.methods?.forEach((method) =>
    queryString.append('filterConfig[methods][]', method)
  );
  filters.complianceStatuses?.forEach((complianceStatus) =>
    queryString.append('filterConfig[complianceStatuses][]', complianceStatus)
  );
  filters.testedPartyEntityIds?.forEach((testedPartyEntityId) =>
    queryString.append(
      'filterConfig[testedPartyEntityIds][]',
      String(testedPartyEntityId)
    )
  );
  filters.lifecycleStatuses?.forEach((lifecycleStatus) =>
    queryString.append('filterConfig[lifecycleStatuses][]', lifecycleStatus)
  );

  if (filters.createdAtStart)
    queryString.set('filterConfig[createdAtStart]', filters.createdAtStart);
  if (filters.createdAtEnd)
    queryString.set('filterConfig[createdAtEnd]', filters.createdAtEnd);

  if (filters.inactive) queryString.set('filterConfig[inactive]', 'true');
  if (filters.visibleInOtp)
    queryString.set('filterConfig[visibleInOtp]', 'true');
}

function buildPoliciesUrl(
  companyId: number,
  serviceProviderEntityIds?: number[],
  period?: TTransferPricingPeriodSelection,
  listQuery?: TPolicyListQueryOptions
): string {
  const queryString = new URLSearchParams();

  if (companyId) {
    queryString.append('companyId', companyId.toString());
  }

  if (serviceProviderEntityIds?.filter(Boolean).length) {
    queryString.append(
      'serviceProviderEntityIds',
      serviceProviderEntityIds.join(',')
    );
  }

  if (period?.periodStart && period?.periodEnd) {
    queryString.append('periodStart', period.periodStart);
    queryString.append('periodEnd', period.periodEnd);
  } else if (period?.periodType && period?.periodStart) {
    queryString.append('periodType', period.periodType);
    queryString.append('periodStart', period.periodStart);
  }

  appendListQuery(queryString, listQuery);

  return `transfer-pricing/policy?${queryString.toString()}`;
}

export function usePolicies(
  companyId: number,
  serviceProviderEntityIds?: number[],
  period?: TTransferPricingPeriodSelection,
  listQuery?: TPolicyListQueryOptions
) {
  const isEnabled = Boolean(companyId) && listQuery?.enabled !== false;

  const URL = isEnabled
    ? buildPoliciesUrl(companyId, serviceProviderEntityIds, period, listQuery)
    : null;

  const {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  }: {
    data?: TPolicyListResponse;
    error?: unknown;
    mutate: KeyedMutator<unknown>;
    isLoading: boolean;
    isValidating: boolean;
  } = useSWR(
    URL,
    SWRFetcher({
      method: 'GET',
    }),
    {
      keepPreviousData: Boolean(listQuery),
    }
  );

  return {
    data,
    error,
    mutate,
    isLoading: isEnabled && isLoading,
    isValidating,
  };
}

const POLICY_PERIOD_REQUEST_URL_SEPARATOR = '\n';

/** The segmented P&L grid's read: one request per period, each carrying the accounts behind every total. */
export function usePoliciesForPeriods(
  companyId: number,
  requests: TPolicyPeriodRequest[]
) {
  const urls = requests.map((request) =>
    buildPoliciesUrl(
      companyId,
      request.serviceProviderEntityIds,
      request.period,
      { includeLineItems: true }
    )
  );

  const key =
    Boolean(companyId) && urls.length > 0
      ? urls.join(POLICY_PERIOD_REQUEST_URL_SEPARATOR)
      : null;

  const {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  }: {
    data?: TPolicyListResponse;
    error?: unknown;
    mutate: KeyedMutator<unknown>;
    isLoading: boolean;
    isValidating: boolean;
  } = useSWR(key, async (joinedUrls: string) => {
    const fetchOne = SWRFetcher({ method: 'GET' });
    const responses: TPolicyListResponse[] = await Promise.all(
      joinedUrls
        .split(POLICY_PERIOD_REQUEST_URL_SEPARATOR)
        .map((url) => fetchOne(url))
    );

    const policies = responses.flatMap((response) => response?.policies ?? []);

    return { policies, count: policies.length };
  });

  return {
    data,
    error,
    mutate,
    isLoading: key !== null && isLoading,
    isValidating,
  };
}
